import { createContext, useContext, useState, useCallback, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { EnquiryForm } from "./EnquiryForm";
import { useLanguage } from "../i18n/LanguageContext";

const EnquiryContext = createContext({ open: () => {} });

export const useEnquiry = () => useContext(EnquiryContext);

export const EnquiryProvider = ({ children }) => {
  const [state, setState] = useState(null);
  const { t } = useLanguage();

  const open = useCallback((opts = {}) => setState(opts), []);
  const close = useCallback(() => setState(null), []);

  useEffect(() => {
    document.body.style.overflow = state ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [state]);

  return (
    <EnquiryContext.Provider value={{ open }}>
      {children}
      <AnimatePresence>
        {state && (
          <motion.div
            data-testid="enquiry-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[90] flex items-end justify-center bg-[#04090F]/80 backdrop-blur-md sm:items-center sm:p-6"
            onClick={close}
          >
            <motion.div
              initial={{ y: 60, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 60, opacity: 0 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="relative max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-t-2xl border border-amber-500/25 bg-[#0A1622] p-6 shadow-2xl sm:rounded-2xl sm:p-10"
            >
              <button
                data-testid="enquiry-modal-close-btn"
                onClick={close}
                aria-label="Close enquiry form"
                className="absolute right-4 top-4 rounded-full border border-amber-500/25 p-2 text-slate-400 transition-colors hover:border-amber-400 hover:text-amber-400"
              >
                <X className="h-4 w-4" />
              </button>
              <p className="text-xs uppercase tracking-[0.28em] font-display font-semibold text-amber-400">
                {t("form.modalOver")}
              </p>
              <h3 className="mt-3 font-serif text-2xl sm:text-3xl text-emerald-50">
                {t("form.modalTitle")}
              </h3>
              <div className="mt-8">
                <EnquiryForm initialProduct={state.product || ""} initialType={state.type || ""} />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </EnquiryContext.Provider>
  );
};
